/* eslint-disable @khanacademy/ts-no-error-suppressions */
/**
 * Renders module encouraging learners to practice.
 */

import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {MediaLayout} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {staticUrl} from "@khan/urls";

type Props = {
    title: string;
    body: React.ReactNode;
    imageAltText: string;
    imageUrl: string;
    mobileImageUrl?: string;
    maxImageWidth: number;
};

export default function PracticeModule({
    title,
    body,
    imageAltText,
    imageUrl,
    mobileImageUrl,
    maxImageWidth,
}: Props): React.ReactElement {
    return (
        <MediaLayout styleSheets={styleSheets}>
            {({mediaSize, styles}) => {
                const isSmall = mediaSize === "small";
                return (
                    <View style={styles.practiceWrapper}>
                        <View
                            style={[styles.practiceBlock, styles.practiceText]}
                        >
                            <h1
                                // This is a <h1> instead of a Wonder Blocks typography
                                // component because the font size does not conform to
                                // Wonder Blocks specifications, since we are aligning
                                // the style of this page with College Board's branding.
                                // @ts-expect-error - TS2345 - Argument of type 'StyleType' is not assignable to parameter of type 'CSSProperties | Falsy'.
                                className={css(styles.practiceTitleText)}
                            >
                                {title}
                            </h1>
                            <Body style={styles.practiceBodyText}>{body}</Body>
                        </View>
                        <View
                            style={[
                                styles.practiceBlock,
                                styles.practiceImage,
                                {maxWidth: maxImageWidth},
                            ]}
                        >
                            <img
                                alt={imageAltText}
                                // This image is hidden from the screenreader since the
                                // corresponding body paragraph already describes the graph,
                                // and we want to avoid redundancy.
                                aria-hidden={true}
                                src={staticUrl(
                                    isSmall && mobileImageUrl != null
                                        ? mobileImageUrl
                                        : imageUrl,
                                )}
                                width="100%"
                            />
                        </View>
                    </View>
                );
            }}
        </MediaLayout>
    );
}

const styleSheets = {
    all: StyleSheet.create({
        practiceBodyText: {
            color: color.offBlack,
            fontSize: 18,
            lineHeight: "30px",
            maxWidth: 460,
        },
        practiceTitleText: {
            color: "black",
            fontFamily: "Source Serif Pro",
            fontWeight: 600,
            "-webkit-font-smoothing": "antialiased",
            "-moz-osx-font-smoothing": "grayscale",
        },
        practiceWrapper: {
            display: "flex",
            justifyContent: "space-between",
            margin: "0 auto",
        },
    }),
    mdOrLarger: StyleSheet.create({
        practiceWrapper: {
            flexDirection: "row",
            maxWidth: 1062,
            paddingTop: 112,
            paddingBottom: 112,
            paddingLeft: 24,
            paddingRight: 24,
            justifyContent: "space-between",
            gridGap: spacing.xxxLarge_64,
        },
        practiceImage: {
            justifyContent: "center",
            width: "125%",
        },
        practiceText: {
            width: "75%",
        },
        practiceTitleText: {
            fontSize: 44,
            lineHeight: "48px",
        },
    }),
    small: StyleSheet.create({
        // Note(nisha): The following styles are used. The linter is confused
        // on the last style so we have to disable eslint.
        // eslint-disable-next-line react-native/no-unused-styles
        practiceWrapper: {
            flexDirection: "column-reverse",
            paddingTop: 64,
            paddingBottom: 64,
            paddingLeft: 24,
            paddingRight: 24,
            backgroundColor: color.offWhite,
        },
        // eslint-disable-next-line react-native/no-unused-styles
        practiceBlock: {
            width: "100%",
            maxWidth: 312,
            margin: "0 auto",
        },
        // eslint-disable-next-line react-native/no-unused-styles
        practiceImage: {
            paddingBottom: 56,
        },
        // eslint-disable-next-line react-native/no-unused-styles
        practiceTitleText: {
            fontSize: 30,
            lineHeight: "36px",
        },
    }),
} as const;
